import React, { useCallback, useContext, useMemo, useRef } from 'react'
import BaseBundleOptionItemTile from 'components/OfferPage/BundleAndSave/BundleAndSaveTiles/BaseBundleOptionItemTile'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import Group from 'components/utils/Group'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import TextLink from 'components/Luxkit/TextLink'
import Image from 'components/Common/Image/Image'
import ModalContext from 'contexts/ModalContext'
import BundleAndSaveHotelTileInclusionsModalView from './BundleAndSaveHotelTileInclusionsModalView'
import styled from 'styled-components'
import { rem } from 'polished'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import moment from 'moment/moment'
import { sortBy } from 'lib/array/arrayUtils'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import { useElementHeight } from 'hooks/useElementSize'
import Pluralize from 'components/Common/Pluralize'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { InclusionsV2Section } from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import { InclusionsSection } from 'components/OfferPage/FlashHotel/InclusionsSection'
import renderInclusions from 'components/OfferPage/AlwaysOn/inclusions/renderInclusions'
import { inclusionTravelPeriodFilter } from 'lib/offer/inclusionsTravelPeriodFilter'

const Container = styled.div`
  display: grid;
  height: 100%;
  grid-template-columns: 1fr;
  grid-template-rows: min-content min-content min-content 1fr;
  gap: ${rem(8)};
`

const Inclusions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content min-content;
  height: 100%;
`

interface Props {
  offer: App.Offer
  position: number
  pkg: App.Package
  packageCount: number
  className?: string
  selectedDuration: number;
  checkIn?: moment.Moment,
  rooms: Array<App.RoomOccupants>;
  availableForPurchase: boolean;
}

function BundleAndSaveHotelTile({ offer, rooms, pkg, position, packageCount, className, selectedDuration, checkIn, availableForPurchase }: Props) {
  const inclusionsRef = useRef(null)
  const textLinkRef = useRef(null)
  const textLinkHeight = useElementHeight(textLinkRef)
  const inclusionsHeight = useElementHeight(inclusionsRef)

  const showModal = useContext(ModalContext)

  const dispatch = useOfferPageDispatch()
  const onAssistChipHandler = useCallback(() => {
    dispatch({
      type: OfferPageActions.toggleFilter,
      filter: 'checkin',
    })
    dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: offer.id })
    dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
  }, [dispatch, offer])

  const sortedAmenities = useMemo(() => sortBy(pkg.roomType.amenities, a => a.name, 'asc'), [pkg.roomType])

  const [inclusions, bonusInclusions] = useMemo(() => {
    const inclusions = inclusionTravelPeriodFilter(pkg.inclusions ?? [], checkIn, checkIn?.clone().add(pkg.duration, 'days'), offer.type === OFFER_TYPE_ALWAYS_ON)
    const bonusInclusions = inclusionTravelPeriodFilter(pkg.bonusInclusions ?? [], checkIn, checkIn?.clone().add(pkg.duration, 'days'), offer.type === OFFER_TYPE_ALWAYS_ON)

    return [inclusions, bonusInclusions]
  }, [checkIn, offer.type, pkg.bonusInclusions, pkg.duration, pkg.inclusions])

  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0

  const onInclusionReveal = () => {
    showModal(<BundleAndSaveHotelTileInclusionsModalView offer={offer} packageView={pkg} inclusions={inclusions} bonusInclusions={bonusInclusions} amenities={sortedAmenities} roomPolicyDescription={pkg.roomPolicyDescription} />)
  }

  return (
    <BaseBundleOptionItemTile className={className} room={pkg.roomType}>
      <Container>
        <Group
          direction="horizontal"
          verticalAlign="center"
          horizontalAlign="space-between"
        >
          <Group direction="horizontal" verticalAlign="center" gap={4}>
            <LineBedIcon size="XS" />
            <BodyText variant="small">
              Hotel {position} of {packageCount}
            </BodyText>
          </Group>
          <AssistChip size="small" variant="filled" onClick={onAssistChipHandler}>
            <Pluralize count={pkg.duration} singular="Night"/>
          </AssistChip>
        </Group>
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium">{offer.property?.name}</BodyText>
            <Heading variant="heading5">{pkg.roomType?.name}</Heading>
            {!!pkg.roomType?.sizeSqm && (
              <div>
                <BodyText as="span" variant="medium" weight="bold">
                  Size:{' '}
                </BodyText>
                <BodyText as="span" variant="medium">
                  {pkg.roomType.sizeSqm} m<sup>2</sup>
                </BodyText>
              </div>
            )}
          </Group>
          {offer.property?.logoImageId && (
            <Image
                  dpr={2}
                  enhancedFormats={false}
                  id={offer.property.logoImageId}
                  width={80}
                  aspectRatio="5:3"
                  alt={`${offer.property.name || 'Vendor'} logo`}
              />
          )}
        </Group>
        <Group direction="vertical">
          {availableForPurchase && offer.type === OFFER_TYPE_HOTEL && (
            <FlashCancellationPolicies
                  cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                  checkInDate={checkIn}
                  offerId={pkg.offerId}
              />
          )}
          {availableForPurchase && offer.type === OFFER_TYPE_ALWAYS_ON && (
            <TAOCancellationPolicies
                  offerId={offer.id}
                  useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies}
                  timezoneOffset={offer.property?.timezoneOffset}
                  uniqueKey={pkg.uniqueKey}
                  checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
                  checkOutDate={checkIn ? checkIn.clone().add(selectedDuration, 'days').format(ISO_DATE_FORMAT) : undefined}
                  occupants={rooms}
                  cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
              />
          )}
        </Group>
        <Inclusions ref={inclusionsRef}>
          <LargeContentToggle hideText maxHeight={inclusionsHeight - textLinkHeight}>
            {offer.type === OFFER_TYPE_ALWAYS_ON && (<>
              {renderInclusions(bonusInclusions as App.PackageInclusion[], inclusions as App.PackageInclusion[])}
            </>)}
            {offer.type === OFFER_TYPE_HOTEL && (<>
              {hasInclusionsV1 && (bonusInclusions || inclusions) && <InclusionsSection inclusions={inclusions as string} bonusInclusions={bonusInclusions as string} />}
              {!hasInclusionsV1 && pkg.inclusionsV2 && <InclusionsV2Section inclusions={pkg.inclusionsV2} />}
            </>)}
          </LargeContentToggle>
          {pkg.roomType &&
            <TextLink ref={textLinkRef} size="caption" onClick={onInclusionReveal}>
              View all inclusions & details
            </TextLink>
          }
        </Inclusions>
      </Container>
    </BaseBundleOptionItemTile>
  )
}

export default withOfferPageState((state, props) => {
  const currentOfferId = props.offer.id
  const currentState = takeOfferState(state, currentOfferId)

  return {
    selectedDuration: currentState.duration,
    rooms: currentState.rooms,
    checkIn: currentState.checkInDate,
  }
})(BundleAndSaveHotelTile)
